import * as React from "react";
import { useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  Text,
  makeStyles,
  tokens,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from "@fluentui/react-components";
import { Mic24Regular } from "@fluentui/react-icons";
import { ConversationProvider } from "@elevenlabs/react";
import { ChatPanel } from "./ChatPanel";

const useStyles = makeStyles({
  container: {
    padding: "16px",
  },
  card: {
    width: "100%",
  },
  cardContent: {
    padding: "0",
    display: "flex",
    flexDirection: "column",
  },
  description: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  messageBar: {
    marginTop: "12px",
  },
});

interface VoiceAgentProps {
  documentId?: string;
  documentName?: string;
  agentId?: string;
  checkRevisions?: () => void;
}

export const VoiceAgent: React.FC<VoiceAgentProps> = ({ documentId, documentName, agentId, checkRevisions }) => {
  const styles = useStyles();

  // No document loaded state
  if (!documentId) {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <CardHeader
            image={<Mic24Regular />}
            header={<Text weight="semibold">Parla con l'Agente</Text>}
            description={<Text className={styles.description}>Conversa con l'AI sul tuo documento</Text>}
          />
          <div className={styles.cardContent}>
            <MessageBar intent="warning" className={styles.messageBar}>
              <MessageBarBody>
                <MessageBarTitle>Documento richiesto</MessageBarTitle>
                Carica prima un documento per poter conversare con l'agente AI.
              </MessageBarBody>
            </MessageBar>
          </div>
        </Card>
      </div>
    );
  }

  // Agent not yet created state
  if (!agentId) {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <CardHeader
            image={<Mic24Regular />}
            header={<Text weight="semibold">Parla con l'Agente</Text>}
            description={<Text className={styles.description}>Documento: {documentName || "Documento"}</Text>}
          />
          <div className={styles.cardContent}>
            <MessageBar intent="warning" className={styles.messageBar}>
              <MessageBarBody>
                <MessageBarTitle>Agente in preparazione</MessageBarTitle>
                L'agente AI per questo documento non è ancora disponibile. Ricarica il documento.
              </MessageBarBody>
            </MessageBar>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <CardHeader
          image={<Mic24Regular />}
          header={<Text weight="semibold">Parla con l'Agente</Text>}
          description={<Text className={styles.description}>Documento: {documentName || "Documento"}</Text>}
        />
        <div className={styles.cardContent}>
          <MicControlledChat agentId={agentId} checkRevisions={checkRevisions} />
        </div>
      </Card>
    </div>
  );
};

/**
 * Wrapper that controls mic muted state at the ConversationProvider level
 * so the microphone starts muted and silence is never sent to the agent.
 */
const MicControlledChat: React.FC<{ agentId: string; checkRevisions?: () => void }> = ({
  agentId,
  checkRevisions,
}) => {
  const [isMuted, setIsMuted] = useState(true);

  const handleMutedChange = useCallback((muted: boolean) => {
    setIsMuted(muted);
  }, []);

  return (
    <ConversationProvider isMuted={isMuted} onMutedChange={handleMutedChange}>
      <ChatPanel agentId={agentId} checkRevisions={checkRevisions} />
    </ConversationProvider>
  );
};

export default VoiceAgent;
